import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Tabs } from "@/components/ui/Tabs";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DataTable } from "@/components/tables/DataTable";
import { toast } from "@/store/toast.store";
import {
  useApproveDriverChange,
  useDriverChanges,
  useRejectDriverChange,
} from "@/features/driverChanges/useDriverChanges";
import type { DriverChangeRequest, DriverChangeStatus } from "@/types/domain";

const TABS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

const TONE: Record<DriverChangeStatus, BadgeTone> = { PENDING: "gold", APPROVED: "teal", REJECTED: "danger" };

const FIELD_LABEL: Record<string, string> = {
  name: "Name",
  email: "Email",
  registrationNumber: "Registration no.",
  model: "Vehicle model",
  color: "Vehicle colour",
  year: "Vehicle year",
  permitNumber: "Permit no.",
  permitExpiry: "Permit expiry",
  insuranceExpiry: "Insurance expiry",
};

const errorMessage = (e: unknown) =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
  (e as Error)?.message ??
  "Something went wrong";

/** Driver edits to name, email and vehicle details: nothing goes live until an admin approves. */
export function DriverChangesCard() {
  const [status, setStatus] = useState<DriverChangeStatus>("PENDING");
  const changes = useDriverChanges(status);
  const approve = useApproveDriverChange();
  const reject = useRejectDriverChange();
  const busy = approve.isPending || reject.isPending;

  const onApprove = (r: DriverChangeRequest) => {
    approve.mutate(r.id, {
      onSuccess: () => toast.success("Change approved and applied"),
      onError: (e) => toast.error(errorMessage(e)),
    });
  };
  const onReject = (r: DriverChangeRequest) => {
    const reason = window.prompt("Reason for rejecting (the driver will see it):");
    if (!reason || reason.trim().length < 3) return;
    reject.mutate(
      { id: r.id, reason: reason.trim() },
      {
        onSuccess: () => toast.success("Change rejected"),
        onError: (e) => toast.error(errorMessage(e)),
      },
    );
  };

  const columns = useMemo<ColumnDef<DriverChangeRequest, unknown>[]>(
    () => [
      {
        header: "Driver",
        id: "driver",
        cell: ({ row: { original: r } }) => (
          <div className="flex flex-col">
            <span>{r.driverName ?? r.driverId}</span>
            {r.driverPhone && <span className="text-xs text-ink-faint">{r.driverPhone}</span>}
          </div>
        ),
      },
      { header: "Type", accessorFn: (r) => (r.kind === "VEHICLE" ? "Vehicle" : "Profile") },
      {
        header: "Requested change",
        id: "changes",
        cell: ({ row: { original: r } }) => (
          <ul className="flex flex-col gap-0.5 text-sm">
            {Object.entries(r.requested).map(([k, v]) => (
              <li key={k}>
                <span className="text-ink-faint">{FIELD_LABEL[k] ?? k}: </span>
                <span className="line-through opacity-60">{String(r.current[k] ?? "—")}</span>
                {" → "}
                <span className="font-medium text-ink">{String(v ?? "—")}</span>
              </li>
            ))}
          </ul>
        ),
      },
      { header: "Requested", accessorFn: (r) => new Date(r.createdAt).toLocaleString("en-IN") },
      {
        header: "Status",
        id: "status",
        cell: ({ row: { original: r } }) => (
          <div className="flex flex-col gap-0.5">
            <Badge tone={TONE[r.status]}>{r.status.charAt(0) + r.status.slice(1).toLowerCase()}</Badge>
            {r.rejectReason && <span className="text-xs text-danger-500">{r.rejectReason}</span>}
          </div>
        ),
      },
      {
        header: "",
        id: "actions",
        cell: ({ row: { original: r } }) =>
          r.status === "PENDING" ? (
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="primary" disabled={busy} onClick={() => onApprove(r)}>
                Approve
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => onReject(r)}>
                Reject
              </Button>
            </div>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busy],
  );

  return (
    <div className="card mt-4 flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">
            Profile change requests{status === "PENDING" ? ` · ${changes.data?.total ?? 0}` : ""}
          </h3>
          <p className="text-xs text-ink-faint">
            Drivers can&apos;t change their name, email, registration number or vehicle details on their own. The old
            values stay live until you approve.
          </p>
        </div>
        <Tabs items={TABS} value={status} onChange={(v) => setStatus(v as DriverChangeStatus)} />
      </div>
      <DataTable
        columns={columns}
        data={changes.data?.items ?? []}
        isLoading={changes.isLoading}
        error={changes.error}
        onRetry={() => changes.refetch()}
        rowKey={(r) => r.id}
        emptyTitle="No change requests here"
      />
    </div>
  );
}
